import React, { useReducer, useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import isMobile from 'ismobilejs';
import { saveAs } from 'file-saver';

import MobileLayout from './MobileLayout';
import DesktopLayout from './DesktopLayout';

import UnsavedHeader from '../../../common/UnsavedHeader';

import {
  fetchGetFeatures,
  fetchGetItemById,
  fetchEditVariant,
  fetchDeleteVariant,
  fetchExportMovements,
  fetchGetEshops,
} from '../../../../actions/items';
import { showModal } from '../../../../actions/modal';

import {
  getVariantTitle,
  getInitialStock,
  getProductEncodedUrl,
  decodeId,
} from '../../../../helpers';

import { PRICES_MOCK } from '../../../../config';
import Adaptive from '../../../common/Adaptive';
import { useRef } from 'react';
import { useAutoTranslation } from '../../../../helpers/useAutoTranslation';

const validationSchema = Yup.object().shape({});

const getInitialState = (props) => {
  const isNew = props.match.params.vid === 'new';
  return {
    isArchived: props.history.location.search.indexOf('?archived') !== -1,
    isNew,
    featuresDidLoaded: false,
    features: [],
    productId: props.match.params.id,
    currentProduct: null,
    variantId: !isNew ? props.match.params.vid : null,
    currentVariant: null,
    eshops: [],
  };
};

const handleSortBarcodes = (barcodes) => {
  if (!barcodes) return;

  return barcodes.sort(function (a, b) {
    if (a.type > b.type) {
      return 1;
    }
    if (a.type < b.type) {
      return -1;
    }
    return 0;
  });
};

const getValuesFromVariant = (variant, warehouses) => {
  return {
    prices: variant.prices
      ? variant.prices.reduce((a, c) => {
          a[c.priceCode] = c;
          return a;
        }, {})
      : PRICES_MOCK,
    weight: variant.weight < 500 ? variant.weight : variant.weight / 1000,
    weightType: variant.weight < 500 ? 'gr' : 'kg',
    dimensions: variant.dimensions || { width: 10, height: 10, depth: 10 },
    article: variant.article,
    stockAvailable: variant.stockAvailable,
    optionsUsed: variant.optionsUsed,
    barcodes: handleSortBarcodes(variant.barcodes) || [
      { value: '', isDefault: true, type: 'COMMON' },
    ],
    extId: variant.extId,
    extId2: variant.extId2,
    extId3: variant.extId3,
    extId4: variant.extId4,
    extId5: variant.extId5,
    stock: variant.stock ? getInitialStock(warehouses, variant.stock) : getInitialStock(warehouses),
    updateStockTotal: false,
    eshopSettings: variant.eshopSettings || null,
    otherCost: variant?.marginParams?.otherCost,
    fulfillmentCost: variant?.marginParams?.fulfillmentCost,
    marketplaceCosts: variant?.marginParams?.marketplaceCosts,
    multiplicity: variant?.multiplicity,
    shortName: variant?.shortName,
    accountingAttributes: variant?.accountingAttributes,
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'setFeatures':
      return { ...state, features: action.features, featuresDidLoaded: true };
    case 'updateProduct':
      return { ...state, currentProduct: action.product };
    case 'updateVariant':
      return {
        ...state,
        currentVariant: action.variant,
        variantId: action.variant.id,
        isNew: false,
      };
    case 'addNewVariant':
      return { ...state, currentVariant: action.variant, variantId: null, isNew: true };
    case 'setEshops':
      return { ...state, eshops: action.eshops };
    default:
      return state;
  }
};

const SingleVariantPage = (props) => {
  const {
    history,
    location,
    fetchGetFeatures,
    fetchGetItemById,
    fetchEditVariant,
    fetchDeleteVariant,
    showModal,
    setCustomHeader,
    fetchGetEshops,
  } = props;

  const arrPathname = location?.pathname.split('/');
  const currentVariantId = arrPathname[arrPathname.length - 1];

  const monoboxesUpdateRef = useRef();

  const reduxDispatch = useDispatch();
  const [state, dispatch] = useReducer(reducer, getInitialState(props));
  const [loading, setLoading] = useState(false);
  const [formikInitialized, setFormikState] = useState(false);
  const [unsaved, setUnsavedStatus] = useState(false);
  const warehouses = useSelector((state) => state.orders.warehouses);

  const productsPriceMarginHistory = useSelector(
    (state) => state?.items?.productsPriceMarginHistory?.history,
  );
  const productMarginShipped = useSelector((state) => state.items.productMarginShipped);
  const productMarginDelivered = useSelector((state) => state.items.productMarginDelivered);

  const monoboxId = location.state?.monoboxId;

  const { t } = useAutoTranslation();

  const {
    isArchived,
    isNew,
    featuresDidLoaded,
    features,
    productId,
    currentProduct,
    variantId,
    currentVariant,
    eshops,
  } = state;

  const formik = useFormik({
    initialValues: {
      stock: getInitialStock(warehouses),
      updateStockTotal: false,
    },
    onSubmit: async () => {
      if (monoboxesUpdateRef.current) {
        await monoboxesUpdateRef.current.updateMonoboxes();
      }
      editVariant();
    },
    validationSchema,
  });

  const { values, errors, handleSubmit, setFieldValue, setValues, isValid } = formik;

  useEffect(() => {
    (async function () {
      const promisesArr = [fetchGetFeatures(), fetchGetEshops()];

      const result = await Promise.all(promisesArr);

      const features = result[0] || [];
      const eshops = result[1].eshops || [];

      dispatch({ type: 'setFeatures', features: features.filter((f) => f.title) });
      dispatch({ type: 'setEshops', eshops });
    })();
  }, []);

  const refreshVariant = async () => {
    const product = await fetchGetItemById(productId, isArchived);
    dispatch({ type: 'updateProduct', product });
    if (!isNew) {
      const findVariant = product.variants.find((v) => v.id === decodeId(currentVariantId));

      if (findVariant) {
        dispatch({ type: 'updateVariant', variant: findVariant });
      } else {
        const customState = location && location.state ? { ...location.state } : undefined;
        history.push(getProductEncodedUrl(productId), customState);
      }
    } else {
      if (location && location.state && location.state.newVariantData) {
        dispatch({
          type: 'addNewVariant',
          variant: { ...location.state.newVariantData, id: null },
        });
      } else {
        history.push(getProductEncodedUrl(productId));
      }
    }
  };

  useEffect(() => {
    (async function () {
      await refreshVariant();
    })();
  }, [featuresDidLoaded, monoboxId]);

  useEffect(() => {
    if (currentVariant) {
      const newValues = getValuesFromVariant(currentVariant, warehouses);

      setValues(newValues);
      setFormikState(true);
    }
  }, [currentVariant]);

  useEffect(() => {
    setCustomHeader(
      unsaved ? (
        <UnsavedHeader handleSubmit={handleSubmit} loading={loading} disabled={!isValid} />
      ) : null,
    );
    return () => {
      setCustomHeader(null);
    };
  }, [unsaved, loading, isValid]);

  const setCurrentVariant = (variantId) => {
    setUnsavedStatus(false);
    const findVariant = currentProduct.variants.find((v) => v.id === decodeId(variantId));
    dispatch({ type: 'updateVariant', variant: findVariant });

    const url = getProductEncodedUrl(productId, variantId, { archived: isArchived });
    history.push(url);
  };

  const handleCloneVariant = () => {
    setUnsavedStatus(false);
    const newVariant = Object.assign({}, values, {
      optionsUsed: values.optionsUsed?.map((o) => ({
        ...o,
        stringValue: null,
        decimalValue: null,
      })),
      prices: Object.keys(values.prices).reduce((a, key) => {
        a.push({
          ...values.prices[key],
          value: Number(values.prices[key].value),
        });
        return a;
      }, []),
    });
    newVariant.weight = values.weightType === 'kg' ? values.weight * 1000 : values.weight;
    dispatch({ type: 'addNewVariant', variant: { ...newVariant, id: null } });

    const url = getProductEncodedUrl(productId, 'new', { archived: isArchived });
    history.push(url);
  };

  const editVariant = (imageUrl) => {
    setLoading(true);

    const getMarketplaceCosts = () => {
      const updatedCosts = values?.marketplaceCosts.map((item) =>
        item.costPercent === '' ? { ...item, costPercent: 0 } : item,
      );
      return updatedCosts;
    };

    const getFulfillmentCost = () => {
      const updatedCost = values?.fulfillmentCost === '' ? 0 : values.fulfillmentCost;
      return updatedCost;
    };

    const getOtherCost = () => {
      const updatedCost = values?.otherCost === '' ? 0 : values.otherCost;
      return updatedCost;
    };

    const fetchData = Object.assign({}, currentVariant, values, {
      productId: decodeId(productId),
      isArchived,
      barcodes: values.barcodes ? values.barcodes.filter((b) => b.value) : values.barcodes,
      eshopSettings:
        values.eshopSettings && values.eshopSettings.length ? values.eshopSettings : [],

      prices: Object.keys(values.prices).reduce((a, key) => {
        a.push({
          ...values.prices[key],
          value: Number(values.prices[key].value),
        });
        return a;
      }, []),
      marginParams: {
        fulfillmentCost: getFulfillmentCost(),
        otherCost: getOtherCost(),
        marketplaceCosts: getMarketplaceCosts(),
      },
    });
    if (values.weightType === 'kg') {
      fetchData.weight = fetchData.weight * 1000;
    }
    if (fetchData.stockAvailable) {
      fetchData.updateStockAvailable = true;
    }
    if (imageUrl) {
      fetchData.defaultImage = imageUrl;
    }
    if (!isNew) {
      fetchData.variantId = variantId;
    }

    delete fetchData.price;
    delete fetchData.oldPrice;
    delete fetchData.fulfillmentCost;
    delete fetchData.otherCost;
    delete fetchData.marketplaceCosts;

    return fetchEditVariant(fetchData)
      .then((data) => {
        dispatch({ type: 'updateProduct', product: data.product });
        if (!isNew) {
          toast.info(t('Вариант успешно обновлен'));
          const newVariant = data.product.variants.find((v) => v.id === data.variantId);
          dispatch({ type: 'updateVariant', variant: newVariant });
        } else {
          toast.info(t('Вариант успешно сохранен'));
          history.push(getProductEncodedUrl(productId, data.variantId));
        }
      })
      .catch((err) => {
        toast.error(err.data.errors[0].message);
      })
      .finally(() => {
        setUnsavedStatus(false);
        setLoading(false);
      });
  };

  const handleExportMovements = async (id) => {
    try {
      const result = await reduxDispatch(fetchExportMovements(id));
      saveAs(result.fileUrl, result.fileID);
    } catch (err) {
      throw new Error(err);
    }
  };

  if (!currentProduct || !currentVariant || !formikInitialized) return null;

  const pageTitle =
    !isNew && currentVariant ? getVariantTitle(currentVariant, features) : t('Новый вариант');

  const breadcrumbs = [
    { title: t('Товары'), route: '/items' },
    {
      title: currentProduct.shortName,
      route: getProductEncodedUrl(currentProduct.id, undefined, { archived: isArchived }),
    },
    { title: pageTitle },
  ];

  const haveProductsPriceMarginHistory = productsPriceMarginHistory?.length > 0;

  const openImportChestnyznakModal = () => {
    dispatch(
      showModal('ImportChestnyznakNumbersModal', {
        id: currentProduct.variants[0].id,
      }),
    );
  };

  const adaptiveProps = {
    pageTitle,
    breadcrumbs,
    currentProduct,
    isNew,
    features,
    currentVariant,
    setCurrentVariant: isMobile().any ? undefined : setCurrentVariant,
    handleCloneVariant: isMobile().any ? undefined : handleCloneVariant,
    isArchived,
    isValid,
    values,
    warehouses,
    setFieldValue,
    productId,
    loading,
    setLoading,
    history,
    fetchDeleteVariant,
    editVariant,
    handleSubmit,
    errors,
    showModal,
    setUnsavedStatus,
    handleExportMovements,
    refreshVariant: isMobile().any ? undefined : refreshVariant,
    eshops,
    productsPriceMarginHistory,
    productMarginShipped,
    productMarginDelivered,
    haveProductsPriceMarginHistory,
    openImportChestnyznakModal,
    monoboxesUpdateRef,
  };

  return (
    <Adaptive
      DekstopComponent={DesktopLayout}
      MobileComponent={MobileLayout}
      adaptiveProps={adaptiveProps}
    />
  );
};

const mapDispatchTopProps = {
  fetchGetFeatures,
  fetchGetItemById,
  fetchEditVariant,
  fetchDeleteVariant,
  showModal,
  fetchGetEshops,
};

export default connect(null, mapDispatchTopProps)(SingleVariantPage);
